/* Custom PDP gallery: scroll-snap track + arrows + thumbnails.
   Swipe is native (touch scroll on the snap track). Deferred, no deps. */
(() => {
  'use strict';

  const init = (gallery) => {
    if (gallery.dataset.pdpGalleryInitialized === 'true') return;
    const track = gallery.querySelector('[data-gallery-track]');
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    const prev = gallery.querySelector('[data-gallery-prev]');
    const next = gallery.querySelector('[data-gallery-next]');
    if (!track || slides.length < 2) return;
    gallery.dataset.pdpGalleryInitialized = 'true';

    let current = 0;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const goTo = (i) => {
      const clamped = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[clamped].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    const setActive = (i) => {
      current = i;
      thumbs.forEach((t, ti) => {
        const isActive = ti === i;
        t.classList.toggle('is-active', isActive);
        if (isActive) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
    };

    prev && prev.addEventListener('click', () => goTo(current - 1));
    next && next.addEventListener('click', () => goTo(current + 1));
    thumbs.forEach((t, i) => t.addEventListener('click', () => goTo(i)));

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(slides.indexOf(e.target));
        });
      },
      { root: track, threshold: 0.6 }
    );
    slides.forEach((s) => io.observe(s));

    // Keyboard support when the gallery is focused.
    gallery.addEventListener('keydown', (e) => {
      const target = e.target instanceof Element ? e.target : null;
      // Native video controls use Left/Right Arrow for seeking. Do not turn a
      // media-control keystroke into a gallery change (the event is retargeted
      // to the <video> element from the browser's controls).
      if (
        e.defaultPrevented ||
        e.altKey ||
        e.ctrlKey ||
        e.metaKey ||
        target?.closest('video, audio, iframe, model-viewer, deferred-media, input, select, textarea, [contenteditable="true"]')
      ) {
        return;
      }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1); }
    });
  };

  // Sticky mobile add-to-cart bar: keep it hidden while the main ATC is still
  // below or inside the viewport. Reveal it only after the shopper scrolls
  // past the main button (CSS hides the whole bar on desktop).
  const initStickyAtc = () => {
    const bar = document.querySelector('[data-sticky-atc]');
    const anchor = document.querySelector('.pdp-buy__atc, .product__info-container .product-form__submit');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    if (bar.dataset.stickyAtcInitialized === 'true') return;
    bar.dataset.stickyAtcInitialized = 'true';

    const proxy = bar.querySelector('[data-sticky-proxy]');
    if (proxy) {
      const syncProxy = () => {
        proxy.disabled = anchor.disabled || anchor.getAttribute('aria-disabled') === 'true';
      };
      proxy.addEventListener('click', () => {
        if (!proxy.disabled) anchor.click();
      });
      new MutationObserver(syncProxy).observe(anchor, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
      });
      syncProxy();
    }

    new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const hasScrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        bar.classList.toggle('is-visible', hasScrolledPast);
      },
      { threshold: 0 }
    ).observe(anchor);
  };

  const boot = () => {
    document.querySelectorAll('[data-pdp-gallery]').forEach(init);
    initStickyAtc();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
